import React, { Fragment, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretUp, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import PropTypes from 'prop-types';

import { requestUploadPreview } from 'api/sfms/rawMaterial/price';
import List from 'components/common-sfims/List';
import Modal from 'components/common-sfims/modal/Common';
import { getText } from 'function/language';
import { RESPONSE_CODE } from 'types/const';
import formatNumber from 'utils/format/number';
import format from 'utils/string/format';

/**
 * 배합비 작성 정보 관리 > 원료 가격 - 마켓 가격 업로드
 */
const UploadMarket = ({ show, uploadData, onHide, onUpload }) => {
  /* state */
  // 목록
  const [datas, setDatas] = useState([]);
  // 목록 끝 번호
  const [end, setEnd] = useState(20);
  // 로딩 여부
  const [isLoading, setIsLoading] = useState(true);
  // 메시지
  const [message, setMessage] = useState({});
  // 총 데이터 수
  const [total, setTotal] = useState(0);

  /* watch */
  useEffect(() => {
    // 모달이 표시된 경우 마켓 가격 업로드 목록 조회 요청
    if (show) {
      requestList();
    }
  }, [show]);

  /* method */
  // 마켓 가격 업로드 목록 조회 요청
  const requestList = async () => {
    setDatas([]);
    setEnd(20);
    setIsLoading(true);
    setMessage({});

    try {
      const {
        list = [],
        totalCount = 0,
        code,
        message
      } = await requestUploadPreview(uploadData);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setDatas(list);
      setTotal(totalCount);
    } catch (error) {
      setMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  /* event */
  // 업로드
  const onSubmit = () => {
    // 업로드 시 이벤트 실행
    onUpload();

    // 모달 숨김
    onHide();
  };

  /* view */
  return (
    <Modal
      show={show}
      className={{ footer: 'align-items-center justify-content-center' }}
      size="lg"
      onHide={onHide}
    >
      <Fragment key="header">{getText('uploadMarketPrice')}</Fragment>
      <Fragment key="body">
        <div id="scroll" className="max-vh-75 scrollbar">
          <InfiniteScroll
            hasMore={end < total}
            dataLength={datas.slice(0, end).length}
            scrollableTarget="scroll"
            next={() => setEnd(end + 20)}
          >
            <List
              className={{
                table: 'mb-0',
                thead: 'sticky-top'
              }}
              columns={[
                'rowNum',
                'pltCode',
                'nationCurr',
                'rwmtrCd',
                'rwmtrNm',
                moment(uploadData.priceYm)
                  .subtract(1, 'months')
                  .format(
                    format(getText('ym'), {
                      year: 'YY',
                      month: 'M'
                    })
                  ),
                moment(uploadData.priceYm).format(
                  format(getText('ym'), {
                    year: 'YY',
                    month: 'M'
                  })
                ),
                'diff'
              ]}
              message={message}
              showLoading={isLoading}
            >
              {datas
                .slice(0, end)
                .map(
                  (
                    {
                      pltCode,
                      nationCurr,
                      rwmtrCd,
                      rwmtrNm,
                      prevPrice,
                      currentPrice,
                      diffPrice
                    } = {},
                    index
                  ) => (
                    <tr key={`datas${index}`}>
                      <td>{total - index}</td>
                      <td>{pltCode}</td>
                      <td>{nationCurr}</td>
                      <td>{rwmtrCd}</td>
                      <td>{rwmtrNm}</td>
                      <td className="text-end">
                        {formatNumber(prevPrice, ',0.00')}
                      </td>
                      <td className="text-end bg-soft-success">
                        {formatNumber(currentPrice, ',0.00')}
                      </td>
                      <td className="text-end text-danger">
                        {diffPrice !== 0 && (
                          <FontAwesomeIcon
                            icon={diffPrice < 0 ? faCaretDown : faCaretUp}
                            className="mr-1"
                          />
                        )}
                        {formatNumber(Math.abs(diffPrice), ',0.00')}
                      </td>
                    </tr>
                  )
                )}
            </List>
          </InfiniteScroll>
        </div>
      </Fragment>
      <Fragment key="footer">
        <Button variant="secondary" onClick={onHide}>
          {getText('cancel')}
        </Button>
        <Button className="ms-2" onClick={onSubmit}>
          {getText('upload')}
        </Button>
      </Fragment>
    </Modal>
  );
};

UploadMarket.propTypes = {
  // 표시 여부
  show: PropTypes.bool,

  // 원료 가격 업로드 요청 데이터
  uploadData: PropTypes.object,

  // 모달을 숨긴 경우 이벤트
  onHide: PropTypes.func,

  // 업로드 시 이벤트
  onUpload: PropTypes.func
};
UploadMarket.defaultProps = {
  show: false,
  uploadData: {}
};

export default UploadMarket;
